import { useRef, useState } from "react";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { uploadPhoto, photoUrl } from "@/lib/reviews";

export const PhotoPicker = ({ photos, setPhotos }) => {
  const { t } = useI18n();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [limit, setLimit] = useState(false);

  const pick = async (e) => {
    const all = [...e.target.files];
    const files = all.slice(0, 3 - photos.length);
    setLimit(all.length > files.length);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setError(false);
    const added = [];
    for (const f of files) {
      try { added.push({ ...(await uploadPhoto(f)), preview: URL.createObjectURL(f) }); } catch { setError(true); }
    }
    setPhotos([...photos, ...added]);
    setBusy(false);
  };

  return (
    <div>
      <p className="text-xs text-white/60 mb-2">{t("review_photos")}</p>
      <div className="flex gap-2 flex-wrap">
        {photos.map((p) => (
          <div key={p.id} data-testid={`review-photo-preview-${p.id}`} className="relative h-20 w-20 bg-white">
            <img src={p.preview} alt="" className="h-full w-full object-cover" />
            <button type="button" data-testid={`review-photo-remove-${p.id}`} onClick={() => { URL.revokeObjectURL(p.preview); setPhotos(photos.filter((x) => x.id !== p.id)); }}
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#0b0a08] border border-white/30 flex items-center justify-center"><X size={12} /></button>
          </div>
        ))}
        {photos.length < 3 && (
          <button type="button" data-testid="review-photo-add-button" onClick={() => input.current.click()} disabled={busy}
            className="h-20 w-20 border border-dashed border-[#c9a55c]/60 flex flex-col items-center justify-center gap-1 text-gold text-[10px] hover:bg-[#c9a55c]/10 transition-colors">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            {busy ? t("review_photo_uploading") : `${photos.length}/3`}
          </button>
        )}
      </div>
      <input ref={input} data-testid="review-photo-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple hidden onChange={pick} />
      {limit && <p data-testid="review-photo-limit" className="text-xs text-white/50 mt-2">{t("review_photo_limit")}</p>}
      {error && <p data-testid="review-photo-error" className="text-xs text-red-400 mt-2">{t("review_photo_error")}</p>}
    </div>
  );
};

export const ReviewPhotos = ({ photos = [], testid }) => {
  const [big, setBig] = useState(null);
  if (!photos.length) return null;
  return (
    <>
      <div className="flex gap-2 mb-4" data-testid={testid}>
        {photos.map((src) => (
          <button key={src} type="button" onClick={() => setBig(src)} className="h-20 w-20 bg-white overflow-hidden">
            <img src={photoUrl(src)} alt="" loading="lazy" className="h-full w-full object-cover hover:scale-110 transition-transform duration-500" />
          </button>
        ))}
      </div>
      {big && (
        <button type="button" data-testid="review-photo-lightbox" onClick={() => setBig(null)} className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-6">
          <img src={photoUrl(big)} alt="" className="max-h-full max-w-full object-contain" />
        </button>
      )}
    </>
  );
};
